import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

// Session timeout duration (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Throttle delay for activity events (5 seconds)
const ACTIVITY_THROTTLE = 5000;

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  
  // Use refs instead of state for timer to prevent re-renders
  const sessionTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isProcessingAuthRef = useRef(false);

  // Reset session timer - using useCallback to maintain stable reference
  const resetSessionTimer = useCallback(() => {
    // Clear existing timer
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
  }, []);

  // Start session timer - separate from reset to avoid stale closures
  const startSessionTimer = useCallback((user, remember) => {
    // Clear any existing timer first
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current);
    }
    
    // Only set timer if user is logged in and "Remember Me" is not checked
    if (user && !remember) {
      sessionTimerRef.current = setTimeout(async () => {
        try {
          await supabase.auth.signOut();
          window.location.href = '/login?timeout=true';
        } catch (err) {
          console.error('Error during session timeout logout:', err);
        }
      }, SESSION_TIMEOUT);
    }
  }, []);

  // Sign up function
  async function signup(email, password) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) {
        setError(error.message);
        throw error;
      }
      
      // Timer will be started by onAuthStateChange
      return data;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }

  // Login function
  async function login(email, password, remember = false) {
    try {
      isProcessingAuthRef.current = true;
      setRememberMe(remember);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        isProcessingAuthRef.current = false;
        setError(error.message);
        throw error;
      }
      
      // Timer will be started by onAuthStateChange
      return data;
    } catch (error) {
      isProcessingAuthRef.current = false;
      setError(error.message);
      throw error;
    }
  }

  // Google sign in
  async function googleSignIn(remember = false) {
    try {
      isProcessingAuthRef.current = true;
      setRememberMe(remember);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });
      
      if (error) {
        isProcessingAuthRef.current = false;
        setError(error.message);
        throw error;
      }
      
      // Timer will be started by onAuthStateChange after redirect
      return data;
    } catch (error) {
      isProcessingAuthRef.current = false;
      setError(error.message);
      throw error;
    }
  }

  // Logout function
  async function logout() {
    try {
      resetSessionTimer();
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        setError(error.message);
        throw error;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }

  // Password reset
  async function resetPassword(email) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) {
        setError(error.message);
        throw error;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }

  // Get current user's access token
  async function getIdToken(forceRefresh = false) {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        throw error;
      }
      
      return data.session?.access_token || null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }

  // Effect for auth state changes
  useEffect(() => {
    let isMounted = true;
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      
      setCurrentUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        startSessionTimer(session.user, rememberMe);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (event === 'TOKEN_REFRESHED') {
        // Token refreshed - only update token in storage, don't update currentUser
        // to avoid re-render loops (new object reference triggers effects that call getSession)
        if (session?.access_token) {
          localStorage.setItem('authToken', session.access_token);
        }
        if (session?.user) {
          startSessionTimer(session.user, rememberMe);
        }
        return;
      }

      if (event === 'SIGNED_IN') {
        // Small delay to let OAuth redirect complete
        setTimeout(() => {
          if (!isMounted) return;
          setCurrentUser(session?.user ?? null);
          setLoading(false);
          isProcessingAuthRef.current = false;

          if (session?.user) {
            startSessionTimer(session.user, rememberMe);
          }
        }, 100);
      } else {
        setCurrentUser(session?.user ?? null);
        setLoading(false);
        isProcessingAuthRef.current = false;

        if (session?.user) {
          startSessionTimer(session.user, rememberMe);
        } else {
          resetSessionTimer();
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      resetSessionTimer();
    };
  }, [rememberMe, startSessionTimer, resetSessionTimer]);

  // Effect for token storage
  useEffect(() => {
    if (currentUser) {
      // Get and store token
      getIdToken().then(token => {
        if (token) {
          localStorage.setItem('authToken', token);
        }
      });
    } else {
      localStorage.removeItem('authToken');
    }
  }, [currentUser]);

  // Effect for user activity monitoring - throttled to prevent excessive updates
  useEffect(() => {
    if (!currentUser || rememberMe) return;

    // Throttled activity handler - only resets timer if enough time has passed
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current >= ACTIVITY_THROTTLE) {
        lastActivityRef.current = now;
        startSessionTimer(currentUser, rememberMe);
      }
    };

    // Add event listeners for user activity
    window.addEventListener('mousemove', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity, { passive: true });
    window.addEventListener('click', handleActivity, { passive: true });
    window.addEventListener('scroll', handleActivity, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, [currentUser, rememberMe, startSessionTimer]);

  const value = {
    currentUser,
    error,
    loading,
    signup,
    login,
    logout,
    googleSignIn,
    resetPassword,
    getIdToken,
    rememberMe,
    setRememberMe
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 